/*
 * Time helpers of the audit filters: the quick ranges, and the round trip
 * between ISO timestamps and `<input type="datetime-local">` values.
 */
import { AuditListParams } from '../../../types/audit';

export type RangeKey = 'lastHour' | 'last24h' | 'last7d' | 'last30d' | 'allTime';

export const QUICK_RANGES: Array<{ key: RangeKey; hours: number | null }> = [
  { key: 'lastHour', hours: 1 },
  { key: 'last24h', hours: 24 },
  { key: 'last7d', hours: 24 * 7 },
  { key: 'last30d', hours: 24 * 30 },
  { key: 'allTime', hours: null },
];

/** The filters with the window set to the last `hours` hours (null clears it). */
export const withQuickRange = (filters: AuditListParams, hours: number | null): AuditListParams => {
  const next: AuditListParams = { ...filters };
  if (hours === null) {
    delete next.from_timestamp;
    delete next.to_timestamp;
  } else {
    const to = new Date();
    next.from_timestamp = new Date(to.getTime() - hours * 3600 * 1000).toISOString();
    next.to_timestamp = to.toISOString();
  }
  return next;
};

/** Which quick range the current window matches, to the minute; null for a custom window. */
export const activeRangeKey = (filters: AuditListParams): RangeKey | null => {
  if (!filters.from_timestamp && !filters.to_timestamp) return 'allTime';
  if (!filters.from_timestamp || !filters.to_timestamp) return null;
  const span = (new Date(filters.to_timestamp).getTime() - new Date(filters.from_timestamp).getTime()) / 3_600_000;
  return QUICK_RANGES.find((r) => r.hours !== null && Math.abs(r.hours - span) < 0.02)?.key ?? null;
};

/** A Date as a `<input type="datetime-local">` value, in local time. */
export const toLocalInputValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** A datetime-local value ("YYYY-MM-DDTHH:MM", local time) as the ISO string the API takes. */
export const toIsoFromInput = (local: string): string | undefined => {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

export const fromIsoToInput = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : toLocalInputValue(d);
};
